/**
 * Позы живых маркеров строений (см. configs/liveFieldMarkers). Только расчет от
 * часов сцены: маркеры пересоздаются на каждое обновление стора, а огонь горит
 * дальше, не начиная заново. seed — у каждой ячейки свой, чтобы соседние маркеры
 * не двигались хором. На игру это не влияет: только вид.
 */

const TWO_PI = Math.PI * 2;

/** Свой сдвиг времени у маркера: от его места на поле */
export const resolveMarkerSeed = (x, y) => Math.abs(x * 0.0137 + y * 0.0071) % 1;

/**
 * Дрожание пламени: сумма несоизмеримых синусов, поэтому не повторяется на глаз
 * @returns {number} от -1 до 1
 */
export const resolveFlicker = (now, seed, speed = 1) => {
  const t = (now / 1000 + seed * 13) * speed;
  return (Math.sin(t * 11.3) + 0.6 * Math.sin(t * 17.9 + 1.3) + 0.4 * Math.sin(t * 27.1 + 2.1)) / 2;
};

/** Язык пламени: вытягивается, сужается и качается */
export const resolveFlamePose = (now, seed) => ({
  scaleX: 1 - 0.12 * resolveFlicker(now, seed + 0.31, 0.9),
  scaleY: 1 + 0.18 * resolveFlicker(now, seed),
  rotation: 0.12 * resolveFlicker(now, seed + 0.57, 0.55),
});

/** Плавный пульс: доля от 0 до 1 */
export const resolvePulseShare = (now, periodMs, seed = 0) => (
  (Math.sin((now / periodMs + seed) * TWO_PI) + 1) / 2
);

/**
 * Частица, поднимающаяся по кругу: искра или клуб дыма
 * @param {number} index — какая из count
 * @returns {{share: number, alpha: number, sway: number}} share — доля пути вверх,
 *   alpha — проявление и затухание, sway — покачивание вбок от -1 до 1
 */
export const resolveRisingPose = (now, seed, index, count, periodMs) => {
  const share = (now / periodMs + seed + index / count) % 1;
  return {
    share,
    alpha: Math.sin(share * Math.PI),
    sway: Math.sin(share * TWO_PI * 1.5 + index * 2.4 + seed * TWO_PI),
  };
};

/**
 * Удар в ближнем бою: резкий всплеск в начале такта и плавный спад
 * @returns {number} от 0 до 1
 */
export const resolveStrikeShare = (now, periodMs, seed) => {
  const share = (now / periodMs + seed) % 1;
  return (1 - share) ** 3;
};

/**
 * Искра удара: вылетает из центра по своему углу и гаснет на лету. Угол на каждый
 * такт новый: искры не повторяют один и тот же рисунок
 * @returns {{angle: number, reach: number, alpha: number}} reach — доля разлета
 */
export const resolveSparkPose = (now, seed, index, count, periodMs) => {
  const beat = now / periodMs + seed;
  const share = beat % 1;
  const angle = (index / count + Math.floor(beat) * 0.37) * TWO_PI;
  return {
    angle,
    reach: 0.45 + 0.55 * (1 - (1 - share) ** 2),
    alpha: (1 - share) ** 1.5,
  };
};

/**
 * Общее мигание: туда, задержка, обратно. Считается от часов, поэтому у всех маркеров
 * одна фаза, когда бы они ни появились
 * @returns {number} от 0 до 1
 */
export const resolveSharedFadeShare = (now, durationMs, holdMs) => {
  const time = now % (durationMs * 2 + holdMs);
  if (time < durationMs) {
    return (1 - Math.cos((time / durationMs) * Math.PI)) / 2;
  }
  if (time < durationMs + holdMs) {
    return 1;
  }
  return (1 + Math.cos(((time - durationMs - holdMs) / durationMs) * Math.PI)) / 2;
};

// Такт молотка от удара к удару: отскок, замах, задержка перед ударом и сам удар
const HAMMER_RECOIL_END = 0.14;
const HAMMER_RAISE_END = 0.72;
const HAMMER_HOLD_END = 0.86;

/**
 * Молоток в руке: поворот вокруг рукояти и сколько прошло от последнего удара
 * @param {number} now
 * @param {number} periodMs — от удара до удара
 * @param {number} seed
 * @param {number} raiseAngle — угол замаха, радианы
 * @param {number} recoilAngle — на сколько боек проходит дальше места удара
 * @returns {{angle: number, share: number, beat: number}} share — доля такта от удара
 */
export const resolveHammerPose = (now, periodMs, seed, raiseAngle, recoilAngle) => {
  const position = now / periodMs + seed;
  const share = position % 1;
  const beat = Math.floor(position);
  let angle;
  if (share < HAMMER_RECOIL_END) {
    angle = recoilAngle * (1 - share / HAMMER_RECOIL_END);
  } else if (share < HAMMER_RAISE_END) {
    const raise = (share - HAMMER_RECOIL_END) / (HAMMER_RAISE_END - HAMMER_RECOIL_END);
    angle = raiseAngle * (1 - (1 - raise) ** 2);
  } else if (share < HAMMER_HOLD_END) {
    angle = raiseAngle;
  } else {
    // Удар ускоряется и приходит как раз к началу такта
    const strike = ((share - HAMMER_HOLD_END) / (1 - HAMMER_HOLD_END)) ** 2;
    angle = raiseAngle + (recoilAngle - raiseAngle) * strike;
  }
  return {angle, share, beat};
};

/**
 * Искра от удара молотка: на каждом ударе свой рисунок
 * @param {number} age — от 0 в момент удара до 1, когда искра погасла
 * @returns {{angle: number, reach: number, alpha: number}}
 */
export const resolveHammerSparkPose = (age, beat, index, count) => ({
  angle: (index / count + beat * 0.37) * TWO_PI,
  reach: 0.3 + 0.7 * (1 - (1 - age) ** 2),
  alpha: (1 - age) ** 1.5,
});

/**
 * Песочные часы: большую часть такта стоят, в конце плавно переворачиваются
 * @returns {number} поворот, радианы
 */
export const resolveHourglassRotation = (now, periodMs, seed) => {
  const position = now / periodMs + seed;
  const share = position % 1;
  const flip = share < 0.75 ? 0 : (1 - Math.cos(((share - 0.75) / 0.25) * Math.PI)) / 2;
  return (Math.floor(position) + flip) * Math.PI;
};

/**
 * Камни шкалы крепости: равномерно по ломаной шеврона, от верхнего конца через
 * вершину к нижнему (урон заполняет шкалу сверху вниз)
 *
 * @param {number} count
 * @param {Object} shape — доли ячейки: apexXFactor, armXFactor, armYFactor
 * @param {number} hexSize
 * @returns {Array<{x: number, y: number}>} относительно центра шеврона
 */
export const resolveFortressPipPositions = (count, {apexXFactor, armXFactor, armYFactor}, hexSize) => (
  Array.from({length: count}, (_, index) => {
    const share = count > 1 ? index / (count - 1) : 0.5;
    const armShare = Math.abs(share - 0.5) * 2;
    return {
      x: hexSize * (apexXFactor + (armXFactor - apexXFactor) * armShare),
      y: hexSize * armYFactor * (share * 2 - 1),
    };
  })
);

/**
 * Блик, бегущий по камням сверху вниз
 * @param {number} pipShare — место камня на шкале от 0 до 1
 * @returns {number} яркость от 0 до 1
 */
export const resolveShimmer = (now, seed, pipShare, periodMs, width) => {
  const head = ((now / periodMs + seed) % 1) * 1.4 - 0.2;
  const distance = Math.abs(pipShare - head) / width;
  return distance < 1 ? (1 - distance) ** 2 : 0;
};
